import { LeasingCalculatorSharedFormProps } from 'modules/leasingCalculator/components/common/LeasingCalculatorData';
import { useLeasingInstallmentCalculation } from 'modules/leasingCalculator/components/infoCalculation/components/common/useLeasingInstallmentCalculation';
import { LeasingType } from 'modules/types/LeasingOffer';
import { InputWithLabel, Subtitle } from 'modules/ui';
import { SliderWithLabel } from 'modules/ui/slider/SliderWithLabel';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { SufixType } from 'modules/ui/input/common/SufixType';
import { parseFromCurrencyString, parseToCurrencyString } from 'modules/utils';
import { numberToLocaleString } from 'modules/utils/numberFormatUtils';
import { useForm } from 'modules/ui/form/useForm';
import { FooterLine, LeasingDataContainer } from '../../styled';
import { LeasingInstallmentInfoCard } from '../common/installmentInfoCard';
import { LeasingInstallmentState } from '../common/LeasingInstallmentState';
import { FooterButtons } from '../common/footerButtons';
import { useLeasingCalculatorCodes } from '../../../../../hooks/codesHooks/useLeasingCalculatorCodes';

const DEFAULT_FINANCIAL_FUTURE_VALUE_PERCENTAGE = 1;

export const FinancialLeasingData: React.FC<
  LeasingCalculatorSharedFormProps
> = ({ defaultValues, onChange }, props) => {
  const { t } = useTranslation();

  const [futureValuePct, setFutureValuePct] = useState<number>(
    +(
      defaultValues?.financialFutureValuePercentage ||
      DEFAULT_FINANCIAL_FUTURE_VALUE_PERCENTAGE
    ),
  );

  const financialInitialValue: LeasingInstallmentState<string> = {
    leasingInstallment: '0',
    leasingParticipationAmount: '0',
    futureValuePercentage: DEFAULT_FINANCIAL_FUTURE_VALUE_PERCENTAGE.toString(),
    futureValue: '0',
  };

  const {
    formData,
    recalculateFutureValuePercentage,
    recalculateParticipationPercentage,
  } = useLeasingInstallmentCalculation(
    defaultValues,
    financialInitialValue,
    LeasingType.FINANCIAL,
    defaultValues?.financialLeasingParticipation,
    futureValuePct,
  );

  const { updateFormData } = useForm({
    onChange,
  });

  const { findLeasingTypeByName } = useLeasingCalculatorCodes();

  // Admin cost: bidirectional percentage <-> amount
  const brutoTotalPrice = useMemo(() => {
    const netPrice = +(defaultValues?.leasingNetObjectPrice || 0);
    const ppmv = +(defaultValues?.leasingObjectPpmv || 0);
    return netPrice * 1.25 + ppmv;
  }, [defaultValues?.leasingNetObjectPrice, defaultValues?.leasingObjectPpmv]);

  const [adminCostAmount, setAdminCostAmount] = useState<string>(() => {
    const pct = +(defaultValues?.financialAdminCostPercentage ?? 0) / 100;
    return numberToLocaleString(pct * brutoTotalPrice);
  });

  useEffect(() => {
    const pct = +(defaultValues?.financialAdminCostPercentage ?? 0) / 100;
    setAdminCostAmount(numberToLocaleString(pct * brutoTotalPrice));
  }, [defaultValues?.financialAdminCostPercentage, brutoTotalPrice]);

  const handleAdminCostAmountChange = (value: string) => {
    const amount = +parseFromCurrencyString(value);
    if (brutoTotalPrice > 0) {
      const newPct = (amount / brutoTotalPrice) * 100;
      updateFormData('financialAdminCostPercentage', newPct);
    }
  };

  useEffect(() => {
    if (onChange) {
      onChange({
        financialLeasingInstallment: formData.leasingInstallment,
        financialParticipationAmount: formData.leasingParticipationAmount,
        financialFutureValuePercentage: formData.futureValuePercentage,
        financialFutureValue: formData.futureValue,
      });
    }
  }, [
    formData.leasingInstallment,
    formData.leasingParticipationAmount,
    formData.futureValuePercentage,
    formData.futureValue,
  ]);

  const handleFutureValuePercentageChange = (value: string) => {
    const parsed = +parseFromCurrencyString(value);
    setFutureValuePct(parsed);
  };

  const handleFutureValueAmountChange = (value: string) => {
    const newPct = recalculateFutureValuePercentage(value);
    if (newPct !== undefined) {
      setFutureValuePct(newPct);
    }
  };

  const handleParticipationAmountChange = (value: string) => {
    const newPct = recalculateParticipationPercentage(value);
    if (newPct !== undefined) {
      updateFormData('financialLeasingParticipation', newPct);
    }
  };

  return (
    <LeasingDataContainer>
      <Subtitle>
        {t('leasingCalculator.infoCalculation.financialLeasing.title')}
      </Subtitle>

      <SliderWithLabel
        label={t(
          'leasingCalculator.infoCalculation.generalData.adminCostPercentage',
        )}
        min={0}
        max={5}
        valueType={SufixType.Percentage}
        defaultValue={defaultValues?.financialAdminCostPercentage}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={(value) =>
          updateFormData(
            'financialAdminCostPercentage',
            parseFromCurrencyString(value),
          )
        }
      />
      <InputWithLabel
        label={t(
          'leasingCalculator.infoCalculation.generalData.adminCostAmount',
        )}
        defaultValue={adminCostAmount}
        sufix={SufixType.Euro}
        onBlur={(e) => handleAdminCostAmountChange(e.target.value)}
      />

      <SliderWithLabel
        label={t(
          'leasingCalculator.infoCalculation.financialLeasing.participationPercentage',
        )}
        labelTooltip={t(
          'leasingCalculator.infoCalculation.financialLeasing.participationPercentageTooltip',
        )}
        min={0}
        max={50}
        valueType={SufixType.Percentage}
        defaultValue={defaultValues?.financialLeasingParticipation}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={(value) =>
          updateFormData(
            'financialLeasingParticipation',
            parseFromCurrencyString(value),
          )
        }
      />
      <InputWithLabel
        label={t(
          'leasingCalculator.infoCalculation.financialLeasing.participationAmount',
        )}
        defaultValue={formData.leasingParticipationAmount}
        sufix={SufixType.Euro}
        onBlur={(e) => handleParticipationAmountChange(e.target.value)}
      />
      <SliderWithLabel
        label={t(
          'leasingCalculator.infoCalculation.financialLeasing.futureValuePercentage',
        )}
        labelTooltip={t(
          'leasingCalculator.infoCalculation.financialLeasing.futureValuePercentageTooltip',
        )}
        min={0}
        max={50}
        valueType={SufixType.Percentage}
        defaultValue={futureValuePct}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={handleFutureValuePercentageChange}
      />
      <InputWithLabel
        label={t(
          'leasingCalculator.infoCalculation.financialLeasing.futureValue',
        )}
        defaultValue={formData.futureValue}
        sufix={SufixType.Euro}
        onBlur={(e) => handleFutureValueAmountChange(e.target.value)}
      />

      <LeasingInstallmentInfoCard
        installmentAmount={formData.leasingInstallment}
        leasingType={LeasingType.FINANCIAL}
      />

      <FooterLine />

      <FooterButtons
        defaultValues={defaultValues}
        onChange={onChange}
        findLeasingTypeByName={findLeasingTypeByName}
        type={LeasingType.FINANCIAL}
      />
    </LeasingDataContainer>
  );
};
